import React, { useState, useRef } from "react";
import { usePreorderSDK } from "./hooks";
import { THEMES, TEST_POINT_ID, getServerUrl } from "./constants";
import type { PostMessageLog, CompletedOrder, ThemeType } from "./types";
import { ConfigConstructor } from "./ConfigConstructor";
import { SDKDemo } from "./SDKDemo";
import "../PreorderPageExample.css";

/**
 * Example showing how to integrate PreorderPage with React Router and theme switching
 */
export const PreorderPageExample: React.FC = () => {
  // SDK hook
  const { sdkLoaded, sdkError, isLoading, sdk, retry, retryCount } =
    usePreorderSDK();

  const [currentTheme, setCurrentTheme] = useState<ThemeType | null>(null);
  const [messageLogs, setMessageLogs] = useState<PostMessageLog[]>([]);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [completedOrder, setCompletedOrder] = useState<CompletedOrder | null>(
    null
  );
  const [sdkOrderResult, setSdkOrderResult] = useState<any>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const logIdCounter = useRef(0);

  // SDK functions
  const handleSDKOpen = async (orderToEdit?: CompletedOrder) => {
    if (!sdk) {
      console.error("SDK not loaded");
      return;
    }

    // Если есть результат заказа, открываем его для редактирования
    const isEditMode = !!orderToEdit || !!sdkOrderResult;
    const targetPointId =
      orderToEdit?.pointId || sdkOrderResult?.pointId || TEST_POINT_ID;
    const orderToEditId = orderToEdit?.id || sdkOrderResult?.id;

    console.log("targetPointId", targetPointId);

    // Add SDK open log
    const sdkOpenLog: PostMessageLog = {
      id: ++logIdCounter.current,
      timestamp: new Date().toLocaleTimeString(),
      event: isEditMode ? "SDK_OPEN_EDIT" : "SDK_OPEN",
      data: {
        pointId: targetPointId,
        isEditMode,
        orderId: orderToEditId,
        orderNumber: orderToEdit?.number || sdkOrderResult?.number,
      },
    };
    setMessageLogs((prev) => [sdkOpenLog, ...prev].slice(0, 100));

    try {
      // Настраиваем SDK перед открытием
      sdk.configure({
        pointId: targetPointId,
        serverUrl: getServerUrl(),
        debug: process.env.NODE_ENV === "development",
        timeout: 3600000, // 1 hour

        // Настройки анимации
        animations: {
          enabled: true,
          animations: [
            {
              type: "fade",
              from: 0,
              to: 1,
              duration: 300,
              easing: "ease-in-out",
            },
            {
              type: "scale",
              from: 0.9,
              to: 1,
              duration: 300,
              easing: "ease-in-out",
            },
          ],
        },

        // Настройки стилей (SDK 1.4.0+ структура)
        styling: {
          overlay: {
            // CSS свойства для overlay элемента
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            zIndex: "10000",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center", // заменяет modal.position
            justifyContent: "center",
          },
          modal: {
            // CSS свойства для modal контейнера
            width: "90vw",
            height: "90vh",
            maxWidth: "1200px",
            maxHeight: "800px",
            borderRadius: "18px",
            backgroundColor: "white",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            border: "none",
          },
        },

        // Логические настройки модального окна
        modal: {
          // position удален, теперь используется alignItems в styling.overlay
          closeOnBackdropClick: true,
          closeOnEscape: true,
        },
      });

      console.log("SDK configured", {
        pointId: targetPointId,
        serverUrl: getServerUrl(),
        debug: process.env.NODE_ENV === "development",
        timeout: 3600000, // 1 hour

        // Настройки анимации
        animations: {
          enabled: true,
          animations: [
            {
              type: "fade",
              from: 0,
              to: 1,
              duration: 300,
              easing: "ease-in-out",
            },
            {
              type: "scale",
              from: 0.9,
              to: 1,
              duration: 300,
              easing: "ease-in-out",
            },
          ],
        },

        // Настройки стилей (SDK 1.4.0+ структура)
        styling: {
          overlay: {
            // CSS свойства для overlay элемента
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            zIndex: "10000",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center", // заменяет modal.position
            justifyContent: "center",
          },
          modal: {
            // CSS свойства для modal контейнера
            width: "90vw",
            height: "90vh",
            maxWidth: "1200px",
            maxHeight: "800px",
            borderRadius: "18px",
            backgroundColor: "white",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
            border: "none",
          },
        },

        // Логические настройки модального окна
        modal: {
          // position удален, теперь используется alignItems в styling.overlay
          closeOnBackdropClick: true,
          closeOnEscape: true,
        },
      });

      // Открываем модальное окно
      const result = await sdk.open(
        isEditMode
          ? {
              pointId: targetPointId,
              serverUrl: getServerUrl(),
              orderId: orderToEditId,
            }
          : {
              pointId: targetPointId,
              serverUrl: getServerUrl(),
            }
      );

      if (result && result.id) {
        setSdkOrderResult(result);
      }
      console.log("SDK order result:", result);

      // Add SDK result log
      const sdkResultLog: PostMessageLog = {
        id: ++logIdCounter.current,
        timestamp: new Date().toLocaleTimeString(),
        event: isEditMode ? "SDK_EDIT_RESULT" : "SDK_RESULT",
        data: result,
      };
      setMessageLogs((prev) => [sdkResultLog, ...prev].slice(0, 100));

      // If successful and not an error, update the completed order
      if (result && result.orderId) {
        setCompletedOrder(result);
      }
    } catch (error) {
      console.error("SDK open error:", error);
      const errorResult = {
        error: error instanceof Error ? error.message : "Unknown error",
      };

      // Add SDK error log
      const sdkErrorLog: PostMessageLog = {
        id: ++logIdCounter.current,
        timestamp: new Date().toLocaleTimeString(),
        event: isEditMode ? "SDK_EDIT_ERROR" : "SDK_ERROR",
        data: errorResult,
      };
      setMessageLogs((prev) => [sdkErrorLog, ...prev].slice(0, 100));
    }
  };

  const handleSDKSetTheme = (theme: ThemeType) => {
    if (sdk) {
      const themeConfig = THEMES[theme];
      sdk.setTheme(themeConfig);
      setCurrentTheme(theme);
      console.log("SDK theme set:", theme);

      // Add SDK theme log
      const sdkThemeLog: PostMessageLog = {
        id: ++logIdCounter.current,
        timestamp: new Date().toLocaleTimeString(),
        event: "SDK_SET_THEME",
        data: { theme, themeConfig },
      };
      setMessageLogs((prev) => [sdkThemeLog, ...prev].slice(0, 100));
    }
  };

  const handleEditOrder = async () => {
    if (!completedOrder || !sdk) return;

    // Use the unified SDK open function for editing
    await handleSDKOpen(completedOrder);
  };

  const clearLogs = () => {
    setMessageLogs([]);
    setExpandedLogId(null);
    logIdCounter.current = 0;
  };

  const handleLogToggle = (logId: number) => {
    const newExpandedId = expandedLogId === logId ? null : logId;
    setExpandedLogId(newExpandedId);

    if (newExpandedId && logsContainerRef.current) {
      // Delay scroll to allow DOM update
      setTimeout(() => {
        const logElement = document.getElementById(`log-${logId}`);
        if (logElement && logsContainerRef.current) {
          const containerTop = logsContainerRef.current.offsetTop;
          const logTop = logElement.offsetTop;
          logsContainerRef.current.scrollTo({
            top: logTop - containerTop,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  return (
    <div className="preorder-example">
      {/* SDK Demo Section */}
      <div className="container">
        <SDKDemo
          sdkLoaded={sdkLoaded}
          sdkError={sdkError}
          isLoading={isLoading}
          sdk={sdk}
          sdkOrderResult={sdkOrderResult}
          retryCount={retryCount}
          onSDKOpen={() => handleSDKOpen()}
          onAddLog={(log) =>
            setMessageLogs((prev) => [log, ...prev].slice(0, 100))
          }
          onRetry={retry}
        />

        {/* Completed Order Section */}
        {completedOrder && (
          <section className="demo-section">
            <h2>Завершенный заказ</h2>
            <div className="completed-order">
              <div className="order-info">
                <h3>Заказ #{completedOrder.number}</h3>
                <p>ID: {completedOrder.id}</p>
                <p>Тип: {completedOrder.type}</p>
                <p>Статус: {completedOrder.status}</p>
                <p>Сумма: {completedOrder.priceTotal.toFixed(2)} ₽</p>
                <p>Время: {new Date(Date.now()).toLocaleString()}</p>
                {completedOrder.comment && (
                  <p>Комментарий: {completedOrder.comment}</p>
                )}
              </div>
              <div className="order-items">
                <h4>Товары:</h4>
                <ul>
                  {completedOrder.items.map((item, index) => {
                    const itemName =
                      item.name || item.productSnapshot.name || "";
                    return (
                      <li key={index}>
                        {itemName} x {item.quantity} - {item.price.toFixed(2)} ₽
                        {item.productVariantProperties &&
                          item.productVariantProperties.length > 0 && (
                            <span>
                              {" "}
                              (
                              {item.productVariantProperties
                                .map(
                                  (v) =>
                                    `${v.productVariantPropertyName}: ${v.productVariantPropertyValueName}`
                                )
                                .join(", ")}
                              )
                            </span>
                          )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="order-actions">
                <button className="edit-order-button" onClick={handleEditOrder}>
                  ✏️ Редактировать заказ
                </button>
                <button
                  className="clear-order-button"
                  onClick={() => setCompletedOrder(null)}
                >
                  🗑️ Очистить
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="logs-section">
          <div className="logs-header">
            <h2>PostMessage логи</h2>
            <button className="clear-logs-button" onClick={clearLogs}>
              Очистить логи
            </button>
          </div>
          <div className="logs-container" ref={logsContainerRef}>
            {messageLogs.length === 0 ? (
              <div className="no-logs">
                Логи пусты. Откройте модальное окно для получения событий
                предзаказа.
              </div>
            ) : (
              messageLogs.map((log) => {
                const isExpanded = expandedLogId === log.id;
                return (
                  <div
                    key={log.id}
                    id={`log-${log.id}`}
                    className={`log-entry ${
                      isExpanded ? "expanded" : "collapsed"
                    }`}
                  >
                    <div
                      className="log-header"
                      onClick={() => handleLogToggle(log.id)}
                    >
                      <div className="log-meta">
                        <span className="log-timestamp">{log.timestamp}</span>
                        <span className="log-event">{log.event}</span>
                        {log.origin && (
                          <span className="log-origin">{log.origin}</span>
                        )}
                      </div>
                      <div className="log-toggle-icon">
                        {isExpanded ? "▼" : "▶"}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="log-payload">
                        <pre>{JSON.stringify(log.data, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
      {/* Config Constructor - показываем только после загрузки SDK */}
      {sdkLoaded ? (
        <ConfigConstructor
          currentTheme={currentTheme}
          onThemeChange={handleSDKSetTheme}
          sdk={sdk}
          onOpenPreorder={(result) => {
            if (result && result.id) {
              setSdkOrderResult(result);
            }
            if (result && result.orderId) {
              setCompletedOrder(result);
            }
          }}
        />
      ) : isLoading ? (
        <section className="demo-section">
          <div className="container">
            <h2>Конструктор конфигурации</h2>
            <div className="sdk-status loading">
              <div className="status-content">
                <div className="spinner"></div>
                <p>Загрузка конструктора...</p>
                <small>Конструктор появится после успешной загрузки SDK</small>
              </div>
            </div>
          </div>
        </section>
      ) : sdkError ? (
        <section className="demo-section">
          <div className="container">
            <h2>Конструктор конфигурации</h2>
            <div className="sdk-status error">
              <div className="status-content">
                <div className="error-icon">⚠️</div>
                <p>Конструктор недоступен</p>
                <small>Сначала необходимо загрузить SDK</small>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
};
